import { Link } from "@tanstack/react-router";
import type { StateInfo } from "@/lib/states";
import { riskAdvice, riskHeadline, riskVar } from "@/lib/risk";
import { RiskBadge, RiskScale } from "./risk-badge";
import { SeasonNow } from "./season-now";
import { StateLocator } from "./state-locator";
import { CountySourceLine } from "./county-source";
import { SOURCES } from "@/lib/sources";

export function Fact({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-border pt-3">
      <dt className="label-caps text-muted-foreground">{term}</dt>
      <dd className="mt-1.5 text-[0.9375rem] leading-relaxed text-foreground">{children}</dd>
    </div>
  );
}

/**
 * Where the season months come from, in a few words with the link. A regional or national
 * figure says so, so nobody reads a neighbour's months as a local survey.
 */
export function SeasonSourceLine({ state }: { state: StateInfo }) {
  const ref = SOURCES[state.seasonSource.sourceId];
  if (!ref) return null;
  const lead =
    state.risk === "low"
      ? "Source:"
      : state.seasonSource.scope === "state"
        ? "Source:"
        : state.seasonSource.scope === "regional"
          ? "No state figure; regional source:"
          : "No state figure; CDC's national guide:";
  return (
    <span className="mt-1 block text-xs leading-snug text-muted-foreground">
      {lead}{" "}
      <a
        href={ref.url}
        target="_blank"
        rel="noopener noreferrer"
        className="link print-no-url"
        title={ref.title}
      >
        {ref.org}
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
    </span>
  );
}

/** A plain list with the tick's spot as the bullet. */
function DotList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1">
      {items.map((t) => (
        <li key={t} className="flex gap-2.5">
          <span
            aria-hidden
            className="mt-[0.6em] h-1.5 w-1.5 shrink-0 rounded-full bg-rust [print-color-adjust:exact]"
          />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * "Hikers & campers, Hunters, Pet owners" -> "Hikers and campers, hunters, and pet owners".
 * Set as the value under a label, so it starts with a capital and reads as a sentence on
 * its own: the old label ("Take extra care if you're") ran into a lowercase list and
 * looked like a typo.
 */
function audienceSentence(items: string[]): string {
  const words = items.map((t, i) => {
    const plain = t.replace(/\s*&\s*/g, " and ");
    return i === 0 ? plain : plain.charAt(0).toLowerCase() + plain.slice(1);
  });
  if (words.length < 2) return words.join("");
  if (words.length === 2) return `${words[0]} and ${words[1]}`;
  // The serial comma keeps "gardeners and yard workers" from running into the next item.
  return `${words.slice(0, -1).join(", ")}, and ${words[words.length - 1]}`;
}

/**
 * The answer to "are Lone Star ticks where I live, and what do I do?", in that order:
 * where the place sits on the scale, a one-line yes/no, when they're active, one thing
 * to do. Set like an answer sheet (a coloured edge for the level, a table of facts)
 * rather than a card of icon-labelled chips.
 */
export function RiskSnapshot({
  state,
  locationNote,
  showLink = false,
  dense = false,
  locator = false,
}: {
  state: StateInfo;
  /** e.g. "ZIP 30301 is in Georgia. This answer covers the whole state." */
  locationNote?: string;
  showLink?: boolean;
  /**
   * Answer only: scale, headline, season, what to do. Used above the fold on the home
   * page, where the habitat and audience lists push the next step off screen.
   */
  dense?: boolean;
  /** Draw a small map of where the state is, top right. Off where a map is already beside it. */
  locator?: boolean;
}) {
  return (
    <article
      className="relative border border-t-[5px] border-border bg-card px-5 pb-6 pt-5 sm:px-8 sm:pb-8 sm:pt-7"
      style={{ borderTopColor: riskVar[state.risk] }}
    >
      {locator && (
        <StateLocator
          abbr={state.abbr}
          level={state.risk}
          className="absolute right-6 top-6 hidden w-32 sm:block lg:right-8 lg:top-7 lg:w-44"
        />
      )}
      <RiskScale
        level={state.risk}
        place={state.name}
        className={locator ? "max-w-md sm:pr-40 lg:pr-0" : "max-w-md"}
      />

      <h2
        className={`mt-6 ${locator ? "sm:pr-40 lg:pr-52" : ""} font-display text-[1.75rem] font-medium leading-[1.12] tracking-[-0.015em] text-foreground sm:text-[2.25rem]`}
      >
        {riskHeadline(state.risk, state.name)}
      </h2>
      {locationNote && <p className="mt-2 text-sm text-muted-foreground">{locationNote}</p>}

      <p className="measure mt-4 text-[0.9375rem] leading-relaxed text-muted-foreground">
        {state.summary}
      </p>

      <dl className="mt-6 grid gap-x-8 gap-y-5 sm:grid-cols-2">
        <Fact term="Peak season">
          <span className="font-display text-xl">{state.peakSeason}</span>
          {state.risk === "low" ? (
            <span className="mt-1 block text-sm text-muted-foreground">
              Other ticks are most active April to September, CDC says.
            </span>
          ) : (
            <SeasonNow
              peakSeason={state.peakSeason}
              className="mt-1 text-sm text-muted-foreground"
            />
          )}
          <SeasonSourceLine state={state} />
        </Fact>
        <Fact term="What to do">{riskAdvice[state.risk]}</Fact>
        {!dense && (
          <>
            <Fact term="Where ticks wait">
              <DotList items={state.habitats} />
            </Fact>
            <Fact term="Who should take extra care">
              <p>{audienceSentence(state.whoShouldBeAware)}.</p>
            </Fact>
          </>
        )}
      </dl>

      {!dense && state.countyData && state.countyData.length > 0 && (
        <div className="mt-7">
          <h3 className="label-caps font-sans text-muted-foreground">
            Named {state.abbr === "LA" ? "parishes" : "counties"}
          </h3>
          <ul className="mt-2 divide-y divide-border border-y border-border">
            {state.countyData.map((c) => (
              <li
                key={c.name}
                className="grid gap-x-6 gap-y-1 py-2.5 sm:grid-cols-[minmax(0,1fr)_auto]"
              >
                <span className="text-[0.9375rem] text-foreground">{c.name}</span>
                <RiskBadge
                  level={c.awarenessLevel}
                  className="text-[0.8125rem] sm:col-start-2 sm:row-start-1"
                />
                <CountySourceLine source={c.source} className="sm:col-span-2" />
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            Each rating gives its source. &ldquo;Alpha-gal hotspot&rdquo; is kept for the counties
            CDC names for their number or rate of suspected cases. Ticks vary within every county.
          </p>
        </div>
      )}

      {showLink && (
        <Link
          to="/states/$state"
          params={{ state: state.slug }}
          className="link mt-6 inline-block text-[0.9375rem] font-semibold"
        >
          More about ticks in {state.name}
        </Link>
      )}
    </article>
  );
}
